'use client';

import { PhoneIcon } from 'icons/PhoneIcon';
import { useEffect, useRef, useState } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';
import { Icon } from '@/app/_components/common/Icon';
import { Image } from '@/app/_components/common/Image';
import { Link } from '@/app/_components/common/Link';
import { RatingComponent as Rating } from '@/app/_components/NDR/Rating';
import { trackGtmEvent } from '@/app/_lib/utils/ndrUtils';
import { cn } from '@/app/_lib/utils/utils';
import type { Review } from '@/app_data/mock/ndrData';
import { trackOptimizelyEvent } from '@/app_lib/optimizely/utils';
import { useOptimizely } from '@/main/lib/hooks/useOptimizely';

const ndrStickyBarVariants = tv({
  defaultVariants: {
    isVisible: false,
  },
  slots: {
    callButton:
      'flex justify-center items-center gap-2 px-8 py-4 bg-green-400 text-white text-base font-medium leading-4 font-graphik-compact md:hidden',
    container:
      'fixed bottom-0 left-0 right-0 z-50 transform transition-transform duration-200 ease-in-out bg-white border-t border-gray-200 shadow-lg',
    ctaButton:
      'bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-full transition-colors flex items-center justify-center gap-2',
    desktopContainer: 'hidden md:flex items-center justify-between gap-4 lg:max-w-[1184px] lg:mx-auto',
    desktopCtaButton: 'px-8 py-3 text-base min-w-[252px]',
    desktopInfoSection: 'flex items-center gap-6',
    desktopLeftSection: 'flex items-center gap-10 min-w-0 flex-1',
    desktopLogo: 'h-11 w-auto',
    desktopRatingWrapper: 'min-w-0 flex-1',
    infoText: 'text-sm',
    infoTitle: 'font-semibold text-base',
    infoWrapper: 'hidden xl:flex flex-col gap-2',
    logo: 'h-auto w-auto',
    logoWrapper: 'flex-shrink-0',
    mobileContainer: 'flex flex-col gap-6 md:hidden',
    mobileCtaButton: 'px-6 py-3 text-sm w-full',
    mobileHeaderRow: 'flex items-center justify-between gap-4',
    mobileLogo: 'h-8 w-auto',
    mobileRatingWrapper: 'min-w-0',
    trigger: 'absolute',
    wrapper: 'w-full mx-auto px-4 py-5 md:px-12 md:py-6',
  },
  variants: {
    isVisible: {
      false: {
        container: 'translate-y-full hidden',
      },
      true: {
        container: 'translate-y-0',
      },
    },
  },
});

interface NdrStickyBarProps extends VariantProps<typeof ndrStickyBarVariants> {
  className?: string;
  onMount: () => void;
  queryParams?: string;
  review: Review;
  stickyButtonPhone: string;
  triggerElement?: React.RefObject<HTMLDivElement>;
  visible?: boolean;
}

interface NdrStickyBarSkeletonProps {
  className?: string;
}

const NdrStickyBar = ({
  className,
  onMount,
  queryParams = '',
  review,
  stickyButtonPhone,
  triggerElement,
  visible,
}: NdrStickyBarProps) => {
  const { optimizely } = useOptimizely();
  const [isVisible, setIsVisible] = useState(visible ?? false);
  const internalTriggerRef = useRef<HTMLDivElement>(null);
  const triggerRef = triggerElement || internalTriggerRef;
  const slots = ndrStickyBarVariants({ isVisible });
  const telLink = `tel:${stickyButtonPhone || ''}`;
  const ctaUrl = `${review.ctas.sticky.url}${queryParams ? `?${queryParams}` : ''}`;

  const handleCallClick = () => {
    trackGtmEvent(telLink);
  };

  const handleCtaClick = () => {
    trackOptimizelyEvent(optimizely, 'ndr_sticky_bar_button_click', {});
    trackGtmEvent(ctaUrl);
  };

  // Handle scroll visibility
  useEffect(() => {
    const handleScroll = () => {
      if (triggerRef.current) {
        const triggerOffset = triggerRef.current.offsetTop;
        const scrollPosition = window.scrollY;
        setIsVisible(scrollPosition > triggerOffset);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial state

    return () => window.removeEventListener('scroll', handleScroll);
  }, [triggerRef]);

  useEffect(() => {
    onMount();
  }, [onMount]);

  const callButton = (
    <Link
      className={slots.callButton()}
      data-cy='ndr-sticky-bar-call-button'
      href={telLink}
      onClick={handleCallClick}
    >
      <PhoneIcon />
      Call {stickyButtonPhone} Now
    </Link>
  );

  const mobileLayout = (
    <div className={slots.mobileContainer()}>
      <div className={slots.mobileHeaderRow()}>
        <div className={slots.logoWrapper()}>
          <Image
            alt={`${review.companyName} logo`}
            className={slots.mobileLogo()}
            data-cy='ndr-sticky-bar-company-logo'
            fullWidth={false}
            height={28}
            src={review.companyLogos.footerCard || ''}
            width={100}
          />
        </div>

        <div className={slots.mobileRatingWrapper()}>
          <Rating
            className='max-w-fit'
            orientation='horizontal'
            rating={review.reviewScore}
            reviewLabel={review.reviewLabel}
            size='small'
          />
        </div>
      </div>

      <Link
        className={cn(slots.ctaButton(), slots.mobileCtaButton())}
        data-cy='ndr-sticky-bar-cta-button'
        href={ctaUrl}
        onClick={handleCtaClick}
      >
        <span>{review.ctas.sticky.text}</span>
        <Icon
          icon='ChevronRight'
          size='sm'
        />
      </Link>
    </div>
  );

  const desktopInfoSection = review.infoText ? (
    <div className={slots.infoWrapper()}>
      {review.infoTitle && (
        <span
          className={slots.infoTitle()}
          data-cy='ndr-sticky-bar-info-title'
        >
          {review.infoTitle}
        </span>
      )}
      {review.infoText && (
        <span
          className={slots.infoText()}
          data-cy='ndr-sticky-bar-info-text'
        >
          {review.infoText}
        </span>
      )}
    </div>
  ) : null;

  const desktopLayout = (
    <div className={slots.desktopContainer()}>
      <div className={slots.desktopLeftSection()}>
        <div className={slots.logoWrapper()}>
          <Image
            alt={`${review.companyName} logo`}
            className={slots.desktopLogo()}
            data-cy='ndr-sticky-bar-company-logo'
            fullWidth={false}
            height={44}
            src={review.companyLogos.footerCard || ''}
            width={200}
          />
        </div>

        <div className={slots.desktopRatingWrapper()}>
          <Rating
            className='max-w-fit'
            orientation='horizontal'
            rating={review.reviewScore}
            reviewLabel={review.reviewLabel}
            size='large'
          />
        </div>
      </div>

      <div className={slots.desktopInfoSection()}>
        {desktopInfoSection}

        <Link
          className={cn(slots.ctaButton(), slots.desktopCtaButton())}
          data-cy='ndr-sticky-bar-cta-button'
          href={ctaUrl}
          onClick={handleCtaClick}
        >
          <span>{review.ctas.sticky.text}</span>
          <Icon
            icon='ChevronRight'
            size='sm'
          />
        </Link>
      </div>
    </div>
  );

  return (
    <div
      className={cn(slots.container(), className)}
      data-cy='ndr-sticky-bar'
    >
      {callButton}
      <div className={slots.wrapper()}>
        {mobileLayout}
        {desktopLayout}
      </div>
    </div>
  );
};

const NdrStickyBarSkeleton = ({ className }: NdrStickyBarSkeletonProps) => {
  const skeletonStars = ['star-1', 'star-2', 'star-3', 'star-4', 'star-5'];

  return (
    <div
      className={cn(
        'fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg animate-pulse',
        className,
      )}
    >
      <div className='max-w-screen-xl mx-auto px-4 py-4'>
        <div className='flex items-center justify-between gap-4'>
          <div className='flex items-center gap-3 flex-1'>
            <div className='w-12 h-8 bg-gray-200 rounded' />
            <div className='flex flex-col gap-1'>
              <div className='h-4 bg-gray-200 rounded w-24' />
              <div className='flex items-center gap-2'>
                <div className='w-8 h-6 bg-gray-200 rounded' />
                <div className='flex gap-1'>
                  {skeletonStars.map((starId) => (
                    <div
                      className='w-3 h-3 bg-gray-200 rounded'
                      key={starId}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className='flex items-center gap-3'>
            <div className='hidden md:block'>
              <div className='h-10 w-32 bg-gray-200 rounded-full' />
            </div>
            <div className='h-10 w-28 bg-gray-200 rounded-full' />
          </div>
        </div>
      </div>
    </div>
  );
};

export { NdrStickyBar, NdrStickyBarSkeleton };
